import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import he from 'he';

import {
  getPartsStrings, getOrderSections, getPartLabel, getPartDescription
} from 'redux/reducers';
import { Part, createPart } from 'models/Part';

import $api from 'api';

import ActionButton from 'components/ActionButton';
import PartImage from 'components/PartImage';

import './OrderReview.css';
import { ADD_PARTS, CHANGE_PART_QUANTITY, REMOVE_PART, RESET_ORDER, VIEW_ORDER_IMAGES } from 'redux/actions';

//const _orderSections = require('../Header/testOrder.json');

const changePartQuantity = (partNo, quantity) => (
  {
    type: CHANGE_PART_QUANTITY,
    partNo,
    quantity
  }
);
const removePart = partNo => ({ type: REMOVE_PART, partNo });

const OrderItem_propTypes = {
  getString: PropTypes.func.isRequired,
  initialMount: PropTypes.bool.isRequired,
  orderItem: PropTypes.shape().isRequired,
  partsStrings: PropTypes.shape().isRequired,
};

const OrderListItem = ({ initialMount, orderItem, getString, partsStrings }) => {
  const [changed, setChanged] = useState(false);
  const [quantityFocused, setQuantityFocused] = useState(false);
  const [quantityValue, setQuantityValue] = useState(orderItem.quantity);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!initialMount) {
      setTimeout(() => {
        setChanged(false);
        setQuantityValue(orderItem.quantity);
      }, 1000);
      setChanged(true);
    }
  }, [orderItem]);

  const onConfirmPartRemoval = () => {
    return window.confirm(getString('warning-remove-part', 'accessories').replace('%%', orderItem.part.partNo));
  };

  const onQuantityBlur = () => {
    const newQuantity = parseInt(quantityValue, 10);
    let newQuantityValue = quantityValue;
    if (!Number.isNaN(newQuantity) && newQuantity !== orderItem.quantity) {
      if (newQuantity > 0) {
        dispatch(changePartQuantity(orderItem.part.partNo, newQuantity));
      } else if (onConfirmPartRemoval()) {
        dispatch(removePart(orderItem.part.partNo));
      } else {
        newQuantityValue = orderItem.quantity;
      }
    } else {
      newQuantityValue = orderItem.quantity;
    }
    setQuantityFocused(false);
    setQuantityValue(newQuantityValue);
  };

  const onQuantityChange = (e) => {
    // update value if it's a number
    setQuantityValue(e.target.value);
  };

  const onQuantityFocus = (e) => {
    e.target.select();
    setQuantityFocused(true);
  };

  const onQuantityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <tr className={classNames({ changed })}>
      <td>
        {he.decode(getPartDescription(orderItem.part.partNo, partsStrings))}
      </td>
      <td className="text-center">
        {orderItem.part.partNo}
      </td>
      <td className={classNames('text-left', { focused: quantityFocused })}>
        <span className="arrow-right" style={{ borderLeftColor: 'white', verticalAlign: 'middle' }} />
        <input
          type="text"
          maxLength="3"
          name=""
          value={quantityValue}
          onBlur={onQuantityBlur}
          onChange={onQuantityChange}
          onFocus={onQuantityFocus}
          onKeyDown={onQuantityKeyDown}
        />
      </td>
    </tr>
  );
};

OrderListItem.propTypes = OrderItem_propTypes;

const OrderListSection = ({
  partsCategory, partsStrings, orderList, getString, initialMount
}) => {
  // Part items should only flash yellow when something happens, not when the page first loads
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
  }, []);

  return (
    <div className="parts-tbl">
      <div
        className="parts-list-hd"
        dangerouslySetInnerHTML={{ __html: getString(partsCategory, 'accessories') }}
      />
      <table cellPadding="0" cellSpacing="0" border="0" width="100%" align="center">
        <tbody>
        <tr>
          <th width="80%">
            {getString('description', 'general')}
          </th>
          <th className="text-center">
            {getString('part-no', 'general')}
          </th>
          <th className="text-center" width="10%">
            {getString('quantity', 'general')}
          </th>
        </tr>
        {
          orderList.map(orderItem => (
            <OrderListItem
              key={orderItem.part.partNo}
              getString={getString}
              orderItem={orderItem}
              partsStrings={partsStrings}
              initialMount={!isMounted.current}
            />
          ))
        }
        </tbody>
      </table>
    </div>
  );
};

OrderListSection.propTypes = {
  orderList: PropTypes.arrayOf(PropTypes.shape).isRequired,
  partsCategory: PropTypes.string.isRequired,
  partsStrings: PropTypes.shape().isRequired,
  getString: PropTypes.func.isRequired
};


const OrderImageItem = ({ initialMount, orderItem, getString, partsStrings }) => {
  const [changed, setChanged] = useState(false);
  const [quantityFocused, setQuantityFocused] = useState(false);
  const [quantityValue, setQuantityValue] = useState(orderItem.quantity);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!initialMount) {
      setTimeout(() => {
        setChanged(false);
      }, 1000);
      setQuantityValue(orderItem.quantity);
      setChanged(true);
    }
  }, [orderItem]);

  const onConfirmPartRemoval = () => {
    return window.confirm(getString('warning-remove-part', 'accessories').replace('%%', orderItem.part.partNo));
  };

  const onQuantityBlur = () => {
    const newQuantity = parseInt(quantityValue, 10);
    let newQuantityValue = quantityValue;
    if (!Number.isNaN(newQuantity) && newQuantity !== orderItem.quantity) {
      if (newQuantity > 0) {
        dispatch(changePartQuantity(orderItem.part.partNo, newQuantity));
      } else if (onConfirmPartRemoval()) {
        dispatch(removePart(orderItem.part.partNo));
      } else {
        newQuantityValue = orderItem.quantity;
      }
    } else {
      newQuantityValue = orderItem.quantity;
    }
    setQuantityFocused(false);
    setQuantityValue(newQuantityValue);
  };

  const onQuantityChange = (e) => {
    // update value if it's a number
    setQuantityValue(e.target.value);
  };

  const onQuantityFocus = (e) => {
    setQuantityFocused(true);
    e.target.select();
  };

  const onQuantityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <div className="col-md-6 col-xs-24 col-sm-12 order-image-item parts-tbl">
      <div className="image-container" id={orderItem.part.imageId}>
        <PartImage part={createPart(orderItem.part)} />
      </div>
      <div className="row row-header">
        <div className="col-md-15 col-xs-15 header-description">
          {getString('description', 'general')}
        </div>
        <div className="col-md-9 col-xs-9 header-quantity">
          {getString('quantity', 'general')}
        </div>
      </div>
      <div className={classNames('row', { changed })}>
        <div className="col-md-16 col-xs-16 image-item-part-description">
          {he.decode(getPartDescription(orderItem.part.partNo, partsStrings))}
        </div>
        <div className={classNames('text-left', 'quantity-chooser', 'col-md-7', 'col-xs-7', { focused: quantityFocused })}>
          <span className="arrow-right" style={{ borderLeftColor: 'white', verticalAlign: 'middle' }} />
          <input
            type="text"
            maxLength="3"
            name=""
            value={quantityValue}
            onBlur={onQuantityBlur}
            onChange={onQuantityChange}
            onFocus={onQuantityFocus}
            onKeyDown={onQuantityKeyDown}
          />
        </div>
      </div>
    </div>
  );
};

OrderImageItem.propTypes = OrderItem_propTypes;

const OrderImageSection = ({
  partsCategory, partsStrings, orderList, getString,
}) => {
  // Part items should only flash yellow when something happens, not when the page first loads
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
  }, []);

  return (
    <div className="parts-tbl">
      <div
        className="parts-list-hd"
        dangerouslySetInnerHTML={{ __html: getString(partsCategory, 'accessories') }}
      />
      <div className="row">
        {
          orderList.map(orderItem => (
            <OrderImageItem
              key={orderItem.part.partNo}
              getString={getString}
              orderItem={orderItem}
              partsStrings={partsStrings}
              initialMount={!isMounted.current}
            />
          ))
        }
      </div>
    </div>
  );
};

OrderImageSection.propTypes = {
  orderList: PropTypes.arrayOf(PropTypes.shape).isRequired,
  partsCategory: PropTypes.string.isRequired,
  partsStrings: PropTypes.shape().isRequired,
  getString: PropTypes.func.isRequired
};

const SearchResults = ({
  foundParts, partsStrings, onSelectResult, highlight
}) => (foundParts.length > 0
  ? (
    <div className="search-results-dropdown">
      {
          foundParts.map((part, idx) => (
            <button
              key={part.partNo}
              type="button"
              onMouseDown={() => onSelectResult(part)}
              className={classNames({ highlight: highlight === idx })}
              dangerouslySetInnerHTML={{ __html: `${part.partNo} - ${getPartLabel(part.partNo, partsStrings)}` }}
            />
          ))
        }
    </div>
  ) : (
    null
  )
);

SearchResults.propTypes = {
  foundParts: PropTypes.arrayOf(PropTypes.instanceOf(Part)).isRequired,
  partsStrings: PropTypes.shape().isRequired,
  onSelectResult: PropTypes.func.isRequired,
  highlight: PropTypes.number.isRequired
};


const addPartToOrder = part => ({ type: ADD_PARTS, newParts: [part] });
const resetOrder = () => ({ type: RESET_ORDER });
const setViewOrderImages = viewOrderImages => ({ type: VIEW_ORDER_IMAGES, viewOrderImages });

const OrderReview = ({}) => {
  const [foundPart, setFoundPart] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchHighlight, setSearchHighlight] = useState(-1);
  const viewOrderImages = useSelector(state => state.preferences.viewOrderImages);
  const dispatch = useDispatch();
  const history = useHistory();

  const parts = useSelector(state => state.parts.loadedParts);
  const partsStrings = useSelector(state => getPartsStrings(state));
  const orderSections = useSelector(state => getOrderSections(state));
  const getString = useSelector(state => state.strings.getString);

  useEffect(() => {
    // record activity
    $api.postActivity('order-review');
  }, []);

  const onBlur = () => {
    // cancel out
    setSearchResults([]);
    setSearchHighlight(-1);
  };

  const onFocus = () => {
    setSearchValue('');
  };

  const onSelectResult = (part) => {
    setFoundPart(part);
    setSearchValue(part.partNo);
    setSearchResults([]);
    setSearchHighlight(-1);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      onSelectResult(searchResults.length > 0 ? searchResults[searchHighlight] : null);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const newHighlight = e.key === 'ArrowDown'
        ? Math.min(searchResults.length - 1, searchHighlight + 1)
        : Math.max(-1, searchHighlight - 1);
      setSearchHighlight(newHighlight);
    } else if (e.key === 'Escape') {
      e.target.blur();
    }
  };

  const onSearchChange = (e) => {
    // find possible parts
    const searchFor = e.target.value;
    const upperCaseSearch = searchFor.toUpperCase();
    const newSearchResults = searchFor.length > 2 ? parts
      .filter(part => part.partNo.indexOf(upperCaseSearch) >= 0
        || getPartDescription(part.partNo, partsStrings)
          .toUpperCase().indexOf(upperCaseSearch) >= 0) : [];
    setFoundPart(null);
    setSearchValue(searchFor);
    setSearchResults(newSearchResults);
    setSearchHighlight(newSearchResults.length > 0 ? 0 : -1);
  };

  const onAddFoundPart = () => {
    dispatch(addPartToOrder(foundPart));
  };

  const onGoBack = () => {
    history.goBack();
  };

  const onRemoveAll = () => {
    if (window.confirm(getString('warning-remove-all', 'accessories'))) {
      dispatch(resetOrder());
    }
  };

  const onToggleView = () => {
    dispatch(setViewOrderImages(!viewOrderImages));
  };

  return (
    <div className="order-review header-border">
      <div className="row">
        <div className="col-md-4 col-sm-5 back-btn-col">
          <button type="button" name="" value="" className="blue-btn" onClick={onGoBack}>
            {getString('back', 'general')}
          </button>
          <button type="button" name="" value="" className="blue-btn" onClick={onRemoveAll}>
            {getString('remove-all', 'general')}
          </button>
          <button type="button" name="" value="" className="blue-btn" onClick={onToggleView}>
            {getString(viewOrderImages ? 'show-list' : 'show-images', 'general')}
          </button>
        </div>
        <div className="col-md-20 col-sm-19">
          <div className="row ma-head">
            <div className="col-md-8 col-sm-8">
              <div id="order-review-title">
                {getString('monitoring-accessories', 'general')}
              </div>
            </div>
            <div className="col-md-12 col-sm-12 col-xs-18">
              <div className="search-part">
                <span>
                  {getString('add', 'general')}
                </span>
                <div className="search-part-in">
                  <input
                    type="text"
                    name=""
                    value={searchValue}
                    placeholder={getString('part-number', 'general')}
                    onBlur={onBlur}
                    onChange={onSearchChange}
                    onKeyDown={onSearchKeyDown}
                    onFocus={onFocus}
                  />
                  <SearchResults
                    foundParts={searchResults.slice(0, 8)}
                    onSelectResult={onSelectResult}
                    highlight={searchHighlight}
                    partsStrings={partsStrings}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-4 col-xs-6" id="add-found-part">
              <ActionButton onHandleClick={onAddFoundPart} enabled={foundPart !== null && searchValue !== ''}>
                <i className="fa fa-plus" />
              </ActionButton>
            </div>
          </div>
          <div className="row">
            <div className="col-md-24">
              {
                orderSections.map(
                  section => viewOrderImages
                    ? (
                      <OrderImageSection
                        key={section.partsCategory}
                        {...section}
                        getString={getString}
                        partsStrings={partsStrings}
                      />
                      ) : (
                      <OrderListSection
                        key={section.partsCategory}
                        {...section}
                        getString={getString}
                        partsStrings={partsStrings}
                      />
                    )
                )
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


OrderReview.propTypes = {};

export default OrderReview;
